import {
  aiFirstPrinciples,
  aiPipelineEvidence,
  PILLAR_EVIDENCE_ID,
  pillarEntries,
} from "../content/portfolio";
import type { PillarId, PillarSelections, Route } from "../types";

export interface JourneyStop {
  id: string;
  route: string;
  label: string;
  context: string;
  aiSectionId?: string;
  pillarId?: PillarId;
  pillarPrincipleId?: string;
  evidence?: true;
}

const aiFirstStops: JourneyStop[] = [
  {
    id: "ai-first-overview",
    route: "/workflow",
    label: "AI-first engineering",
    context: "Pillar 1 of 4 · overview",
    aiSectionId: "overview",
  },
  ...aiFirstPrinciples.map((principle, index) => ({
    id: `ai-first-${principle.id}`,
    route: "/workflow",
    label: principle.label,
    context: `AI-first engineering · principle ${index + 1} of ${
      aiFirstPrinciples.length
    }`,
    aiSectionId: principle.id,
  })),
  {
    id: "ai-first-evidence",
    route: "/workflow",
    label: aiPipelineEvidence.label,
    context: "AI-first engineering · evidence",
    aiSectionId: aiPipelineEvidence.id,
    evidence: true,
  },
];

const pillarStops: JourneyStop[] = pillarEntries.flatMap(
  (pillar, pillarIndex) => [
    {
      id: `pillar-${pillar.id}`,
      route: `/pillar/${pillar.id}`,
      label: pillar.label,
      context: `Pillar ${pillarIndex + 2} of 4 · overview`,
      pillarId: pillar.id,
    },
    ...pillar.principles.map((principle, principleIndex) => ({
      id: `pillar-${pillar.id}-${principle.id}`,
      route: `/pillar/${pillar.id}`,
      label: principle.label,
      context: `${pillar.label} · principle ${principleIndex + 1} of ${
        pillar.principles.length
      }`,
      pillarId: pillar.id,
      pillarPrincipleId: principle.id,
    })),
    {
      id: `pillar-${pillar.id}-evidence`,
      route: `/pillar/${pillar.id}`,
      label: `${pillar.label} evidence`,
      context: `${pillar.label} · evidence`,
      pillarId: pillar.id,
      pillarPrincipleId: PILLAR_EVIDENCE_ID,
      evidence: true,
    },
  ],
);

export const journeyStops: JourneyStop[] = [
  {
    id: "home",
    route: "/",
    label: "Curiosity Engine",
    context: "Guided orbit",
  },
  {
    id: "about",
    route: "/about",
    label: "About Johnny",
    context: "Start here · personal context",
  },
  ...aiFirstStops,
  ...pillarStops,
  {
    id: "contact",
    route: "/contact",
    label: "Contact",
    context: "End of guided orbit",
  },
];

function getCurrentIndex(
  route: Route,
  pillarSelections: PillarSelections,
  selectedAiSection: string | null,
): number | null {
  if (route.kind === "index" || route.kind === "read") return null;
  if (route.kind === "home") return 0;

  if (route.kind === "workflow") {
    if (selectedAiSection === "pipeline") return null;
    const sectionId = selectedAiSection ?? "overview";
    const aiIndex = journeyStops.findIndex(
      (stop) => stop.aiSectionId === sectionId,
    );
    return aiIndex >= 0
      ? aiIndex
      : journeyStops.findIndex(
          (stop) => stop.aiSectionId === "overview",
        );
  }

  if (route.kind === "pillar") {
    const selectedPrinciple = pillarSelections[route.slug] ?? null;
    const index = journeyStops.findIndex(
      (stop) =>
        stop.pillarId === route.slug &&
        (selectedPrinciple
          ? stop.pillarPrincipleId === selectedPrinciple
          : stop.pillarPrincipleId === undefined),
    );
    return index >= 0
      ? index
      : journeyStops.findIndex(
          (stop) =>
            stop.pillarId === route.slug &&
            stop.pillarPrincipleId === undefined,
        );
  }

  const routePath =
    route.kind === "about" ? "/about" : "/contact";
  const index = journeyStops.findIndex((stop) => stop.route === routePath);
  return index >= 0 ? index : null;
}

export function getJourneyState(
  route: Route,
  pillarSelections: PillarSelections = {},
  selectedAiSection: string | null = null,
) {
  const currentIndex = getCurrentIndex(
    route,
    pillarSelections,
    selectedAiSection,
  );
  if (currentIndex === null) return null;

  return {
    current: journeyStops[currentIndex],
    previous: journeyStops[currentIndex - 1] ?? null,
    next: journeyStops[currentIndex + 1] ?? null,
    position: currentIndex + 1,
    total: journeyStops.length,
  };
}
